import { ButtonGroup, HStack } from '@chakra-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { REDO_COMMAND, UNDO_COMMAND } from 'lexical';
import { FC, useCallback } from 'react';
import { FormatButton } from './FormatButton';
import { LinkButton } from './LinkButton';
import { ListButton } from './ListButton';
import { ToolbarButton } from './ToolbarButton';
import { ImageButton } from './ImageButton';
import { InfoButton } from './InfoButton';
import { useTranslation } from 'react-i18next';

type ToolbarProps = {
  allowImages: boolean;
  allowLinks: boolean;
  allowLists: boolean;
};

export const Toolbar: FC<ToolbarProps> = ({ allowImages, allowLinks, allowLists }) => {
  const { t } = useTranslation('html-editor');
  const [editor] = useLexicalComposerContext();

  const handleUndo = useCallback(() => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  }, [editor]);
  const handleRedo = useCallback(() => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  }, [editor]);

  return (
    <HStack borderBottom="default" padding={1} spacing={1}>
      <ButtonGroup size="md" isAttached>
        <FormatButton format="bold" title={t('htmlEditor.toolbar.bold')}>
          <span style={{ fontWeight: 'bold' }}>B</span>
        </FormatButton>
        <FormatButton format="italic" title={t('htmlEditor.toolbar.italic')}>
          <span style={{ fontStyle: 'italic' }}>I</span>
        </FormatButton>
        <FormatButton format="underline" title={t('htmlEditor.toolbar.underlined')}>
          <span style={{ textDecoration: 'underline' }}>U</span>
        </FormatButton>
      </ButtonGroup>
      {allowLists && (
        <ButtonGroup size="md" isAttached>
          <ListButton listType="bullet" title={t('htmlEditor.toolbar.unorderedList')}>
            <span>•</span>
          </ListButton>
          <ListButton listType="number" title={t('htmlEditor.toolbar.orderedList')}>
            <span>1.</span>
          </ListButton>
        </ButtonGroup>
      )}
      <ButtonGroup size="md" isAttached>
        {allowLinks && <LinkButton />}
        {allowImages && <ImageButton />}
      </ButtonGroup>
      <ButtonGroup size="md" isAttached>
        <ToolbarButton type="button" onClick={handleUndo} title={t('htmlEditor.toolbar.undo')}>
          <span>↶</span>
        </ToolbarButton>
        <ToolbarButton type="button" onClick={handleRedo} title={t('htmlEditor.toolbar.redo')}>
          <span>↷</span>
        </ToolbarButton>
      </ButtonGroup>

      <ButtonGroup size="md" isAttached ml="auto">
        <InfoButton allowLinks={allowLinks} allowLists={allowLists} />
      </ButtonGroup>
    </HStack>
  );
};
